import React from 'react';
import { Button, Alert, Badge, ListGroup } from '@/components/ui';
import ResponsiveModal from './ResponsiveModal';
import { DuplicateFormWarning } from '../services/formService';

interface DuplicateFormWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  duplicateWarning: DuplicateFormWarning | null;
}

const DuplicateFormWarningModal: React.FC<DuplicateFormWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  duplicateWarning
}) => {
  if (!duplicateWarning) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateTimeString: string) => {
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'Unknown';
    }
  };

  const formatDuration = (hours: string) => {
    try {
      const numHours = parseFloat(hours);
      if (isNaN(numHours)) return 'Unknown';
      return `${numHours.toFixed(2)} hours`;
    } catch {
      return 'Unknown';
    }
  };

  const footer = (
    <div className="tw-flex tw-gap-2">
      <Button
        type="button"
        variant="secondary"
        onClick={onClose}
      >
        Cancel
      </Button>
      <Button
        type="button"
        variant="warning"
        onClick={onConfirm}
      >
        Proceed with Approval
      </Button>
    </div>
  );

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicate Daily Log Warning"
      footer={footer}
    >
      <div className="duplicate-warning-content">
        {/* Warning Message */}
        <Alert variant="warning" className="tw-flex tw-items-center tw-mb-3">
          <i className="bi bi-exclamation-triangle-fill tw-mr-2 tw-text-2xl"></i>
          <div>
            <strong>Warning:</strong> {duplicateWarning.message}
          </div>
        </Alert>

        {/* Existing Forms List */}
        <div className="tw-mb-3">
          <h6 className="tw-font-bold tw-mb-2">
            <i className="bi bi-file-earmark-text tw-mr-2"></i>
            Existing Approved Forms:
          </h6>
          <ListGroup>
            {duplicateWarning.duplicateForms.map((form, index) => (
              <ListGroup.Item key={form.id}>
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-grow">
                    <div className="tw-text-content-muted tw-text-sm tw-mb-1">
                      {form.name}
                    </div>
                    <div className="tw-mt-2">
                      <Badge bg="info" text="dark" className="tw-mr-2">
                        <i className="bi bi-person tw-mr-1"></i>
                        {form.employeeName}
                      </Badge>
                      <Badge bg="secondary" className="tw-mr-2">
                        <i className="bi bi-calendar3 tw-mr-1"></i>
                        {formatDate(form.formDate)}
                      </Badge>
                      <Badge bg="success" className="tw-mr-2">
                        <i className="bi bi-clock tw-mr-1"></i>
                        {formatTime(form.startTime)}
                      </Badge>
                      <Badge bg="warning" text="dark">
                        <i className="bi bi-hourglass-split tw-mr-1"></i>
                        {formatDuration(form.duration)}
                      </Badge>
                    </div>
                  </div>
                  <div className="tw-text-right">
                    <small className="tw-text-content-muted">
                      Created: {formatDate(form.createdAt)}
                    </small>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>

        {/* Confirmation Message */}
        <Alert variant="light" className="tw-border tw-border-line">
          <div className="tw-flex tw-items-start">
            <i className="bi bi-question-circle tw-mr-2 tw-text-warning-text tw-mt-1"></i>
            <div>
              <strong>Do you want to proceed with the approval anyway?</strong>
              <div className="tw-text-sm tw-text-content-muted tw-mt-1">
                This will approve the current form despite the existing approved submissions for the same employee and date.
              </div>
            </div>
          </div>
        </Alert>
      </div>
    </ResponsiveModal>
  );
};

export default DuplicateFormWarningModal;